import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {GoogleMap, MarkerF} from '@react-google-maps/api';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useGoogleMaps} from './GoogleMapsContext';
import {
    DEFAULT_MAP_PIN_COORDINATE,
    formatPinnedLocationLabel,
    getMapEventCoordinate,
    normalizeMapPinCoordinate,
    parseGooglePlacesMapResults,
} from './mapPinUtils';

const MAP_CONTAINER_STYLE = {height: '60vh', minHeight: '360px', width: '100%'};
const MAP_OPTIONS = {
    clickableIcons: true,
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
};
const SEARCH_RADIUS_METERS = 50000;
const SEARCH_RESULT_MARKER_COLOR = '#007bff';
const SEARCH_RESULT_MARKER_PATH = [
    'M18 0',
    'C8.06 0 0 8.06 0 18',
    'C0 31.5 18 48 18 48',
    'C18 48 36 31.5 36 18',
    'C36 8.06 27.94 0 18 0',
    'Z',
].join(' ');
const SEARCH_STATUS_MESSAGES = {
    empty: 'No matching places found. You can still place the pin manually.',
    error: 'Couldn\u2019t load matching places. You can still place the pin manually.',
    loading: 'Searching nearby places\u2026',
};

const getInitialSelection = (initialLocation) => {
    const initialCoordinate = normalizeMapPinCoordinate(initialLocation);

    return {
        address: String(initialLocation?.address ?? '').trim()
            || formatPinnedLocationLabel(initialCoordinate ?? DEFAULT_MAP_PIN_COORDINATE),
        coordinate: initialCoordinate ?? DEFAULT_MAP_PIN_COORDINATE,
        isSelectable: Boolean(initialCoordinate),
        placeName: String(
            initialLocation?.place_name ?? initialLocation?.placeName ?? '',
        ).trim(),
    };
};

const isGoogleStatus = (status, enumValue, fallback) => (
    status === enumValue || status === fallback
);

const NPMapPinPicker = ({
    initialLocation = null,
    locationType = 'pickup',
    onConfirm,
    onHide,
    searchQuery,
    show = false,
}) => {
    const {isLoaded, loadError} = useGoogleMaps();
    const initialSelection = useMemo(
        () => getInitialSelection(initialLocation),
        [initialLocation],
    );
    const [map, setMap] = useState(null);
    const [coordinate, setCoordinate] = useState(initialSelection.coordinate);
    const [address, setAddress] = useState(initialSelection.address);
    const [placeName, setPlaceName] = useState(initialSelection.placeName);
    const [isSelectable, setIsSelectable] = useState(initialSelection.isSelectable);
    const [addressStatus, setAddressStatus] = useState('idle');
    const [placeSearch, setPlaceSearch] = useState({results: [], status: 'idle'});
    const geocodeRequestIdRef = useRef(0);
    const searchRequestIdRef = useRef(0);
    const userHasPositionedPinRef = useRef(false);
    const wasShownRef = useRef(false);

    const normalizedSearchQuery = String(searchQuery ?? '').trim();
    const isPickup = locationType !== 'dropoff';
    const locationLabel = isPickup ? 'pickup' : 'drop-off';
    const searchResultMarkerIcon = useMemo(() => {
        const Point = window.google?.maps?.Point;
        if (!isLoaded || !Point) return undefined;

        return {
            anchor: new Point(18, 48),
            fillColor: SEARCH_RESULT_MARKER_COLOR,
            fillOpacity: 1,
            labelOrigin: new Point(18, 16),
            path: SEARCH_RESULT_MARKER_PATH,
            scale: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
        };
    }, [isLoaded]);

    useEffect(() => {
        const becameVisible = show && !wasShownRef.current;
        wasShownRef.current = show;

        if (!show) {
            geocodeRequestIdRef.current += 1;
            searchRequestIdRef.current += 1;
            return;
        }

        if (becameVisible) {
            userHasPositionedPinRef.current = false;
            setCoordinate(initialSelection.coordinate);
            setAddress(initialSelection.address);
            setPlaceName(initialSelection.placeName);
            setIsSelectable(initialSelection.isSelectable);
            setAddressStatus('idle');
            setPlaceSearch({results: [], status: 'idle'});
        }
    }, [initialSelection, show]);

    const reverseGeocode = useCallback((nextCoordinate) => {
        const requestId = geocodeRequestIdRef.current + 1;
        geocodeRequestIdRef.current = requestId;
        const fallbackAddress = formatPinnedLocationLabel(nextCoordinate);

        setAddress(fallbackAddress);
        setAddressStatus('loading');

        if (!window.google?.maps?.Geocoder) {
            setAddressStatus('error');
            return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({location: nextCoordinate}, (results, status) => {
            if (geocodeRequestIdRef.current !== requestId) return;

            const okStatus = window.google.maps.GeocoderStatus?.OK;
            const formattedAddress = String(results?.[0]?.formatted_address ?? '').trim();
            if (isGoogleStatus(status, okStatus, 'OK') && formattedAddress) {
                setAddress(formattedAddress);
                setAddressStatus('success');
            } else {
                setAddressStatus('error');
            }
        });
    }, []);

    const positionPin = useCallback((nextCoordinate) => {
        const normalizedCoordinate = normalizeMapPinCoordinate(nextCoordinate);
        if (!normalizedCoordinate) return null;

        userHasPositionedPinRef.current = true;
        map?.panTo?.(normalizedCoordinate);
        setCoordinate(normalizedCoordinate);
        setIsSelectable(true);
        return normalizedCoordinate;
    }, [map]);

    const selectCoordinate = useCallback((nextCoordinate) => {
        const normalizedCoordinate = positionPin(nextCoordinate);
        if (!normalizedCoordinate) return false;

        reverseGeocode(normalizedCoordinate);
        return true;
    }, [positionPin, reverseGeocode]);

    const selectSearchResult = useCallback((result) => {
        const normalizedCoordinate = positionPin(result?.position);
        if (!normalizedCoordinate) return false;

        geocodeRequestIdRef.current += 1;
        setAddress(result.address || formatPinnedLocationLabel(normalizedCoordinate));
        setPlaceName(result.placeName || '');
        setAddressStatus('success');
        return true;
    }, [positionPin]);

    const selectPoiEvent = useCallback((event, nextCoordinate) => {
        const placeId = String(event?.placeId ?? '').trim();
        if (!placeId) return false;

        event.stop?.();
        const normalizedCoordinate = positionPin(nextCoordinate);
        if (!normalizedCoordinate) return true;

        const requestId = geocodeRequestIdRef.current + 1;
        geocodeRequestIdRef.current = requestId;
        const fallbackAddress = formatPinnedLocationLabel(normalizedCoordinate);
        setAddress(fallbackAddress);
        setPlaceName('');
        setAddressStatus('loading');

        const fallBackToReverseGeocode = () => {
            if (geocodeRequestIdRef.current === requestId) {
                reverseGeocode(normalizedCoordinate);
            }
        };
        const PlacesService = window.google?.maps?.places?.PlacesService;
        if (!map || !PlacesService) {
            fallBackToReverseGeocode();
            return true;
        }

        const service = new PlacesService(map);
        if (typeof service.getDetails !== 'function') {
            fallBackToReverseGeocode();
            return true;
        }

        service.getDetails({
            fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types'],
            placeId,
        }, (place, status) => {
            if (geocodeRequestIdRef.current !== requestId) return;

            const placesStatus = window.google.maps.places.PlacesServiceStatus;
            const [resolvedPlace] = parseGooglePlacesMapResults([{
                ...place,
                place_id: place?.place_id || placeId,
            }], 1);

            if (!isGoogleStatus(status, placesStatus?.OK, 'OK') || !resolvedPlace) {
                fallBackToReverseGeocode();
                return;
            }

            selectSearchResult({
                ...resolvedPlace,
                placeName: resolvedPlace.placeName || String(place?.name ?? '').trim() || null,
            });
        });

        return true;
    }, [map, positionPin, reverseGeocode, selectSearchResult]);

    const selectMapEvent = useCallback((event) => {
        const nextCoordinate = getMapEventCoordinate(event);
        if (!nextCoordinate) return;
        if (!selectPoiEvent(event, nextCoordinate)) {
            selectCoordinate(nextCoordinate);
        }
    }, [selectCoordinate, selectPoiEvent]);

    const selectMapCenter = useCallback(() => {
        const mapCenter = normalizeMapPinCoordinate(map?.getCenter?.());
        selectCoordinate(mapCenter ?? coordinate);
    }, [coordinate, map, selectCoordinate]);

    useEffect(() => {
        const requestId = searchRequestIdRef.current + 1;
        searchRequestIdRef.current = requestId;

        if (!show || !map || !normalizedSearchQuery) {
            setPlaceSearch({results: [], status: 'idle'});
            return undefined;
        }

        const PlacesService = window.google?.maps?.places?.PlacesService;
        if (!PlacesService) {
            setPlaceSearch({results: [], status: 'error'});
            return undefined;
        }

        setPlaceSearch({results: [], status: 'loading'});
        const service = new PlacesService(map);
        service.textSearch({
            location: initialSelection.coordinate,
            query: normalizedSearchQuery,
            radius: SEARCH_RADIUS_METERS,
            region: 'ca',
        }, (results, status) => {
            if (searchRequestIdRef.current !== requestId) return;

            const placesStatus = window.google.maps.places.PlacesServiceStatus;
            if (isGoogleStatus(status, placesStatus?.ZERO_RESULTS, 'ZERO_RESULTS')) {
                setPlaceSearch({results: [], status: 'empty'});
                return;
            }
            if (!isGoogleStatus(status, placesStatus?.OK, 'OK')) {
                setPlaceSearch({results: [], status: 'error'});
                return;
            }

            const parsedResults = parseGooglePlacesMapResults(results);
            setPlaceSearch({
                results: parsedResults,
                status: parsedResults.length > 0 ? 'success' : 'empty',
            });

            if (
                parsedResults.length > 0
                && !userHasPositionedPinRef.current
                && window.google.maps.LatLngBounds
            ) {
                const bounds = new window.google.maps.LatLngBounds();
                parsedResults.forEach((result) => bounds.extend(result.position));
                map.fitBounds?.(bounds, 60);
            }
        });

        return () => {
            if (searchRequestIdRef.current === requestId) {
                searchRequestIdRef.current += 1;
            }
        };
    }, [initialSelection.coordinate, map, normalizedSearchQuery, show]);

    const handleConfirm = () => {
        if (!isSelectable) return;

        onConfirm?.(
            address || formatPinnedLocationLabel(coordinate),
            coordinate,
            placeName || null,
            true,
        );
    };

    const searchStatusMessage = SEARCH_STATUS_MESSAGES[placeSearch.status];

    return (
        <Modal
            centered
            onHide={onHide}
            show={show}
            size="xl"
        >
            <Modal.Header closeButton>
                <Modal.Title>{`Pin ${locationLabel} location`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loadError ? (
                    <div className="alert alert-danger" role="alert">
                        Map could not be loaded. Check the Google Maps key and try again.
                    </div>
                ) : !isLoaded ? (
                    <div className="d-flex align-items-center justify-content-center p-5">
                        <Spinner animation="border" role="status" />
                        <span className="ml-2">Loading map…</span>
                    </div>
                ) : (
                    <>
                        <div className="position-relative">
                            <GoogleMap
                                center={initialSelection.coordinate}
                                mapContainerStyle={MAP_CONTAINER_STYLE}
                                onClick={selectMapEvent}
                                onLoad={setMap}
                                onUnmount={() => setMap(null)}
                                options={MAP_OPTIONS}
                                zoom={13}
                            >
                                {placeSearch.results.map((result, index) => (
                                    <MarkerF
                                        icon={searchResultMarkerIcon}
                                        key={result.placeId}
                                        label={{
                                            color: '#ffffff',
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            text: `${index + 1}`,
                                        }}
                                        onClick={() => selectSearchResult(result)}
                                        position={result.position}
                                        title={result.placeName || result.label}
                                    />
                                ))}
                                <MarkerF
                                    draggable
                                    label={{
                                        color: '#f3f4f6',
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        text: isPickup ? 'A' : 'B',
                                    }}
                                    onDragEnd={selectMapEvent}
                                    position={coordinate}
                                    title={isPickup ? 'Pickup' : 'Drop-off'}
                                    zIndex={1000}
                                />
                            </GoogleMap>
                        </div>

                        <div className="align-items-center d-flex flex-wrap justify-content-between mt-3">
                            <p className="text-muted mb-2 mr-2">
                                Click a place name, click the map, or drag the pin to the exact location.
                            </p>
                            <button
                                aria-label={`Drop ${locationLabel} pin at map center`}
                                className="btn btn-sm btn-outline-primary mb-2"
                                disabled={!map}
                                onClick={selectMapCenter}
                                type="button"
                            >
                                <i className="fa fa-crosshairs mr-1" aria-hidden="true" />
                                Drop pin at map center
                            </button>
                        </div>

                        {searchStatusMessage && (
                            <div
                                aria-live="polite"
                                className={`alert mb-2 ${placeSearch.status === 'error' ? 'alert-warning' : 'alert-info'}`}
                                data-testid="map-place-search-status"
                            >
                                {placeSearch.status === 'loading' && (
                                    <Spinner animation="border" className="mr-2" size="sm" />
                                )}
                                {searchStatusMessage}
                            </div>
                        )}

                        {placeName && (
                            <div className="font-weight-bold" data-testid="map-pin-place-name">
                                {placeName}
                            </div>
                        )}
                        <div aria-live="polite" data-testid="map-pin-address">
                            {address}
                        </div>
                        <div className="small text-muted" data-testid="map-pin-coordinate-label">
                            {formatPinnedLocationLabel(coordinate)}
                        </div>

                        {addressStatus === 'loading' && (
                            <div className="small text-muted" aria-live="polite">
                                Finding the closest address…
                            </div>
                        )}
                        {addressStatus === 'error' && (
                            <div className="small text-warning" aria-live="polite">
                                Couldn’t look up an address. The exact pinned coordinates will be used.
                            </div>
                        )}
                        {!isSelectable && (
                            <div
                                className="small text-warning mt-2"
                                data-testid="map-pin-selection-prompt"
                            >
                                Click a place name, click the map, or drag the pin before using this location.
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={onHide} type="button">
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    disabled={
                        !isLoaded
                        || Boolean(loadError)
                        || !isSelectable
                        || addressStatus === 'loading'
                    }
                    onClick={handleConfirm}
                    type="button"
                >
                    <i className="fa fa-map-marker mr-1" aria-hidden="true" />
                    Use this location
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default NPMapPinPicker;
